// ─── PtzControlPanel ─────────────────────────────────────────────────────────
// Overlay para controlar PTZ de una cámara ONVIF Profile P.
//
// Props:
//   cameraConfig — { host, port, username, password, profile_token }
//   token        — JWT
//   onClose      — cerrar el overlay
//
// El operador puede hacer pan/tilt continuo (mouse down → mantiene moviendo →
// mouse up frena), zoom +/-, y disparar presets.
//
// IMPORTANTE: las credenciales viajan en el body del POST (HTTPS). Para el
// piloto las guardamos en localStorage por (host:port). Para enterprise mover
// a tabla camera_credentials con encrypted at rest.

import { useEffect, useRef, useState } from 'react'

const PRESET_SLOTS = 6 // máximo de presets visibles en grid 3x2

export default function PtzControlPanel({ cameraConfig, token, onClose }) {
  const [presets, setPresets]   = useState([])
  const [busy, setBusy]         = useState(false)
  const [error, setError]       = useState(null)
  const movingRef = useRef(false)

  // Cargar presets al montar
  useEffect(() => {
    if (!cameraConfig?.host) return
    let cancelled = false
    fetch('/api/ptz/presets', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(cameraConfig),
    })
      .then(r => r.ok ? r.json() : { presets: [] })
      .then(j => { if (!cancelled) setPresets(j.presets || []) })
      .catch(e => { if (!cancelled) setError(`Presets: ${e.message}`) })
    return () => { cancelled = true }
  }, [cameraConfig?.host, cameraConfig?.port, cameraConfig?.profile_token, token])

  // Helper: continuous_move + stop pattern. Mouse down dispara move,
  // mouse up/leave dispara stop. Velocidad fija en 0.5 para piloto.
  async function startMove(pan, tilt, zoom = 0) {
    if (movingRef.current) return
    movingRef.current = true
    try {
      await fetch('/api/ptz/continuous_move', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ...cameraConfig, pan, tilt, zoom }),
      })
    } catch (e) {
      setError(`Move: ${e.message}`)
      movingRef.current = false
    }
  }
  async function stopMove() {
    if (!movingRef.current) return
    try {
      await fetch('/api/ptz/stop', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ...cameraConfig, pan_tilt: true, zoom: true }),
      })
    } catch (e) {
      setError(`Stop: ${e.message}`)
    }
    movingRef.current = false
  }
  async function gotoPreset(token_id) {
    setBusy(true)
    try {
      await fetch('/api/ptz/goto_preset', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ...cameraConfig, preset_token: token_id, speed: 0.6 }),
      })
    } catch (e) { setError(`Goto: ${e.message}`) }
    setBusy(false)
  }
  async function savePreset() {
    const name = window.prompt('Nombre del preset:')?.trim()
    if (!name) return
    setBusy(true)
    try {
      const r = await fetch('/api/ptz/set_preset', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ...cameraConfig, name }),
      })
      const j = await r.json()
      if (j.preset_token) {
        setPresets(prev => [...prev, { token: j.preset_token, name }])
      }
    } catch (e) { setError(`Save: ${e.message}`) }
    setBusy(false)
  }

  const dirBtn = (label, pan, tilt, zoom = 0) => (
    <button
      onMouseDown={(e) => { e.stopPropagation(); startMove(pan, tilt, zoom) }}
      onMouseUp={(e) => { e.stopPropagation(); stopMove() }}
      onMouseLeave={() => { if (movingRef.current) stopMove() }}
      onTouchStart={(e) => { e.stopPropagation(); startMove(pan, tilt, zoom) }}
      onTouchEnd={(e) => { e.stopPropagation(); stopMove() }}
      style={{
        width: 44, height: 44, borderRadius: 6,
        background: 'rgba(255,255,255,0.12)', color: '#fff',
        border: '1px solid rgba(255,255,255,0.3)',
        fontSize: 18, fontWeight: 700, cursor: 'pointer',
        userSelect: 'none',
      }}
      title={label}
    >{label}</button>
  )

  return (
    <div
      onClick={(e) => e.stopPropagation()}
      style={{
        position: 'absolute', top: 10, left: 10, zIndex: 15,
        background: 'rgba(10,10,10,0.94)',
        border: '1.5px solid rgba(255,255,255,0.4)', borderRadius: 8,
        padding: 12, color: '#fff', minWidth: 260, maxWidth: 320,
        boxShadow: '0 6px 28px rgba(0,0,0,0.75)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 10 }}>
        <span style={{ fontWeight: 700, fontSize: 13, flex: 1 }}>Control PTZ</span>
        <button onClick={onClose} style={{
          background: 'transparent', color: '#fff', border: 'none',
          fontSize: 16, cursor: 'pointer', padding: 0, width: 24, height: 24,
        }}>✕</button>
      </div>

      {/* Direccional 3x3 + zoom al lado */}
      <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 44px)', gap: 4 }}>
          <div />{dirBtn('▲', 0, 0.5)}<div />
          {dirBtn('◀', -0.5, 0)}{dirBtn('■', 0, 0)}{dirBtn('▶', 0.5, 0)}
          <div />{dirBtn('▼', 0, -0.5)}<div />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {dirBtn('+', 0, 0, 0.5)}
          <div style={{ fontSize: 10, color: '#aaa', textAlign: 'center', padding: '2px 0' }}>ZOOM</div>
          {dirBtn('−', 0, 0, -0.5)}
        </div>
      </div>

      {/* Presets */}
      <div style={{ marginTop: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 6 }}>
          <span style={{ fontSize: 11, fontWeight: 600, color: '#bbb', flex: 1 }}>
            Presets ({presets.length})
          </span>
          <button
            onClick={savePreset}
            disabled={busy}
            style={{
              fontSize: 10, padding: '4px 10px', borderRadius: 4,
              background: 'rgba(37,99,235,0.6)', color: '#fff',
              border: 'none', cursor: busy ? 'not-allowed' : 'pointer',
            }}
          >+ Guardar posición</button>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 4 }}>
          {presets.slice(0, PRESET_SLOTS).map(p => (
            <button
              key={p.token}
              onClick={() => gotoPreset(p.token)}
              disabled={busy}
              style={{
                padding: '7px 4px', fontSize: 10, fontWeight: 600,
                background: 'rgba(255,255,255,0.1)', color: '#fff',
                border: '1px solid rgba(255,255,255,0.2)',
                borderRadius: 4, cursor: busy ? 'not-allowed' : 'pointer',
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}
              title={p.name}
            >{p.name || p.token.slice(0, 6)}</button>
          ))}
          {presets.length === 0 && (
            <div style={{
              gridColumn: '1 / -1', fontSize: 10, color: '#888',
              textAlign: 'center', padding: 10,
            }}>Sin presets guardados</div>
          )}
        </div>
      </div>

      {error && (
        <div style={{
          marginTop: 8, padding: 6, fontSize: 10,
          background: 'rgba(239,68,68,0.18)', color: '#fca5a5',
          borderRadius: 4,
        }}>{error}</div>
      )}
    </div>
  )
}
